import { getKinsCircleConstitution } from '@/lib/governor/KinsCircleConstitution';
// =====================================================================
// KinsCircle Phase 13 — Graph Mutation Gateway (REAL ENFORCEMENT)
// =====================================================================
//
// CONTRACTUAL SINGLE ENTRY POINT for every write that can affect:
//   • family_graph_members rows
//   • parent_ids
//   • spouse_id
//   • claimed_by_user_id
//   • relationship_override
//
// Every UI / context / service that needs to mutate the graph MUST call
// one of the `gateway*` functions below. Direct `supabase.from('family_
// graph_members').insert/update/delete(...)` calls are prohibited and
// will be flagged by the System Health "Graph Enforcement Active" check.
//
// ENFORCEMENT GUARANTEES (all run BEFORE the DB write):
//
//   1. Duplicate prevention — `runDuplicatePreflight` is consulted on
//      every insert / spouse-add / claim. STRONG matches with no
//      `allowDuplicate: true` flag are blocked.
//
//   2. Circular ancestry prevention — `set-parents` walks parent_ids
//      bidirectionally and refuses any edge that would put the
//      member in their own ancestor chain. `set-spouse` runs
//      `wouldSpouseEdgeCreateCircularAncestry`.
//
//   3. Self-* prevention — self-as-parent, self-as-child, self-as-spouse
//      are all rejected with stable blocker codes.
//
//   4. One auth user → max one claimed member — `claim-member` queries
//      `family_graph_members` for any other row already claimed by this
//      auth user and refuses if found. Mirrors the DB unique partial
//      index.
//
//   5. One member → max one claimed auth user — `claim-member` reads
//      the target row and refuses if `claimed_by_user_id` is set to a
//      different user. The UPDATE itself uses `.is('claimed_by_user_id',
//      null)` to close the race window.
//
//   6. Spouse / co-parent edges remain LATERAL — the gateway calls
//      `evaluateSpouseAddition` / `wouldSpouseEdgeCreateCircularAncestry`
//      and refuses any edge that would corrupt parent_ids. parent_ids
//      is never touched by `set-spouse`.
//
//   7. Wrong-tree relationship prevention — `set-parents` confirms every
//      proposed parent id lives on the SAME `owner_user_id` as the
//      member being updated. Cross-graph parent edges are blocked.
//
//   8. Audit logging — every attempt (allowed / blocked / failed) is
//      recorded via `recordGraphMutation` with before/after snapshots,
//      the actor's user id, and a stable blocker code on rejection.
//
// TRANSACTION SAFETY — multi-write operations (set-spouse with two
// rows, claim-member with the verified update + race-safe UPDATE) use
// best-effort rollback when a follow-up step fails. Supabase REST does
// not expose multi-statement transactions to the client, so we use the
// strongest available approach: race-safe `.is(... null)` guards on
// claim writes, server-trusted post-write verification reads, and
// reverse-write rollback when verification fails.
//
// PURE PHILOSOPHY — every callable here is a pure async function. No
// React. No context. The caller passes in the supabase client instance
// + the acting auth user's id + the loaded members snapshot. This makes
// the gateway equally callable from FamilyGraphContext, ad-hoc repair
// scripts, the system-health diagnostics, and the test harness.

import type { SupabaseClient } from '@supabase/supabase-js';
import { v4 as uuidv4 } from 'uuid';
import type {
  GraphMember,
  Relationship,
  Side,
  TreeRelationshipWorkflow,
} from '@/contexts/FamilyGraphContext';
import {
  runDuplicatePreflight,
  type DuplicateSource,
} from '@/lib/duplicates/globalDuplicateGuard';
import {
  evaluateSpouseAddition,
  wouldSpouseEdgeCreateCircularAncestry,
} from '@/lib/relationships/spouseProtection';
import { validateGraphIntegrity } from '@/lib/integrity/graphIntegrityEngine';
import {
  recordGraphMutation,
  type GraphMutationAuditEntry,
} from './graphMutationAudit';
import {
  authorizeGatewayTreeMutation,
} from '@/governor/tree/TreeMutationGatewayAuthority';
import type {
  TreeMutationProposal,
} from '@/governor/tree/TreeMutationProposalBuilder';
import type {
  TreeMutationMode,
} from '@/governor/tree/MandatoryTreeMutationAuthority';
import {
  buildGenericUpdateProposal,
  buildInsertProposal,
  buildRestoreProposal,
  mapDatabaseRowToMandatoryMember,
} from '@/governor/tree/TreeGatewayProposalMapper';
import {
  authorizeCanonicalTreeErrorOnlyDeletion,
  type CanonicalTreeDeletionDependencyEvidence,
  type CanonicalTreeDeletionErrorClass,
} from '@/governor/tree/CanonicalTreeErrorOnlyDeletionAuthority';


// ─── Stable blocker codes (machine-readable) ──────────────────────────
export const BLOCKER = {
  AUTH_REQUIRED: 'auth-required',
  EMPTY_NAME: 'empty-name',
  SELF_AS_PARENT: 'self-as-parent',
  SELF_AS_CHILD: 'self-as-child',
  SELF_AS_SPOUSE: 'self-as-spouse',
  DUPLICATE_BLOCKED: 'duplicate-blocked',
  CIRCULAR_ANCESTRY: 'circular-ancestry',
  PARENT_CHILD_AS_SPOUSE: 'parent-child-overlap',
  WRONG_TREE_PARENT: 'wrong-tree-parent',
  ALREADY_CLAIMED_OTHER: 'target-already-claimed-by-other',
  USER_ALREADY_BOUND: 'user-already-bound-to-other',
  ROW_NOT_FOUND: 'row-not-found',
  DB_ERROR: 'db-error',
  TREE_MUTATION_BLOCKED: 'tree-mutation-blocked',
  DELETION_BLOCKED: 'deletion-blocked',
  DEPENDENCY_EVIDENCE_UNAVAILABLE:
    'dependency-evidence-unavailable',
} as const;

export type BlockerCode = (typeof BLOCKER)[keyof typeof BLOCKER];

// ─── Compact snapshot helper (PII-minimal) ────────────────────────────
function snapshotMember(m: GraphMember | null | undefined): Record<string, unknown> | null {
  if (!m) return null;
  return {
    id: m.id,
    ownerUserId: m.ownerUserId,
    name: m.name,
    relationship: m.relationship,
    side: m.side,
    parentIds: m.parentIds ?? [],
    spouseId: m.spouseId ?? null,
    claimedByUserId: m.claimedByUserId ?? null,
    profileState: m.profileState,
    relationshipOverride: m.relationshipOverride ?? null,
  };
}

function snapshotRow(r: any): Record<string, unknown> | null {
  if (!r) return null;
  return {
    id: r.id,
    ownerUserId: r.owner_user_id,
    name: r.name,
    relationship: r.relationship,
    side: r.side,
    parentIds: r.parent_ids ?? [],
    spouseId: r.spouse_id ?? null,
    claimedByUserId: r.claimed_by_user_id ?? null,
    profileState: r.profile_state ?? null,
    relationshipOverride: r.relationship_override ?? null,
  };
}

// ─── Result types ─────────────────────────────────────────────────────
export interface GatewayBlockedResult {
  ok: false;
  blocker: BlockerCode;
  reason: string;
  audit: GraphMutationAuditEntry;
}

export interface GatewayAllowedResult<T = unknown> {
  ok: true;
  data: T;
  audit: GraphMutationAuditEntry;
}

export type GatewayResult<T = unknown> =
  | GatewayBlockedResult
  | GatewayAllowedResult<T>;

// ─── Common context every gateway call accepts ────────────────────────
export interface GatewayContext {
  supabase: SupabaseClient;
  /** auth.users.id of the actor — null is allowed but most ops will refuse. */
  actingUserId: string | null;
  /** Currently loaded members snapshot for client-side validation. */
  members: GraphMember[];
  /**
   * Claimed family_graph_members.id for the authenticated viewer.
   * This must be supplied explicitly and must never be inferred from
   * names, relationship labels, parent order, or renderer fallbacks.
   */
  viewerMemberId: string | null;
  /** When true, the caller has already shown a duplicate confirmation modal
   *  and the user explicitly said "create anyway". Skips the duplicate block. */
  allowDuplicate?: boolean;
  /** Optional: which call site initiated this (used by the duplicate guardrail
   *  to pick STRICT vs DEFAULT policy). Defaults to 'add-graph-member'. */
  source?: DuplicateSource;
}


// ─── Helpers ──────────────────────────────────────────────────────────
function block(
  kind: GraphMutationAuditEntry['kind'],
  ctx: GatewayContext,
  targetId: string | null,
  before: Record<string, unknown> | null,
  blocker: BlockerCode,
  reason: string,
): GatewayBlockedResult {
  const audit = recordGraphMutation({
    kind,
    actingUserId: ctx.actingUserId,
    targetId,
    before,
    after: null,
    outcome: 'blocked',
    blocker,
    reason,
  });
  return { ok: false, blocker, reason, audit };
}

function authorizeProposedMutation(
  kind: GraphMutationAuditEntry['kind'],
  ctx: GatewayContext,
  targetId: string | null,
  before: Record<string, unknown> | null,
  proposal:
    | TreeMutationProposal
    | (() => TreeMutationProposal),
  mode: TreeMutationMode = 'normal',
): GatewayBlockedResult | null {
  let authorization;

  try {
    const resolvedProposal =
      typeof proposal === 'function'
        ? proposal()
        : proposal;

    authorization = authorizeGatewayTreeMutation({
      members: ctx.members,
      viewerMemberId: ctx.viewerMemberId,
      proposal: resolvedProposal,
      mode,
    });
  } catch (error) {
    return block(
      kind,
      ctx,
      targetId,
      before,
      BLOCKER.TREE_MUTATION_BLOCKED,
      error instanceof Error
        ? `Tree proposal could not be constructed: ${error.message}`
        : 'Tree proposal could not be constructed.',
    );
  }

  if (authorization.decision.allowed) {
    return null;
  }

  return block(
    kind,
    ctx,
    targetId,
    before,
    BLOCKER.TREE_MUTATION_BLOCKED,
    [
      'Mandatory Tree mutation authority rejected the proposed graph.',
      ...authorization.decision.blockers,
    ].join(' '),
  );
}

function fail(
  kind: GraphMutationAuditEntry['kind'],
  ctx: GatewayContext,
  targetId: string | null,
  before: Record<string, unknown> | null,
  reason: string,
  err?: unknown,
): GatewayBlockedResult {
  const audit = recordGraphMutation({
    kind,
    actingUserId: ctx.actingUserId,
    targetId,
    before,
    after: null,
    outcome: 'failed',
    blocker: BLOCKER.DB_ERROR,
    reason: reason + (err instanceof Error ? ` (${err.message})` : ''),
  });
  return { ok: false, blocker: BLOCKER.DB_ERROR, reason: audit.reason ?? reason, audit };
}

function allow<T>(
  kind: GraphMutationAuditEntry['kind'],
  ctx: GatewayContext,
  targetId: string | null,
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
  data: T,
): GatewayAllowedResult<T> {
  const audit = recordGraphMutation({
    kind,
    actingUserId: ctx.actingUserId,
    targetId,
    before,
    after,
    outcome: 'allowed',
    blocker: null,
    reason: null,
  });
  return { ok: true, data, audit };
}


/*
 * CC-TREE-LIVE-003G3WJ3J5BH
 *
 * Browser universal-envelope transport.
 *
 * This helper carries mutation intent only. Authenticated caller identity is
 * supplied by the existing Supabase browser session and is independently
 * re-derived inside the deployed Edge Function.
 *
 * Raw canonical snapshots, trusted actor identity, owner identity, viewer
 * identity, service-role authority, evidence, and attestation data are never
 * accepted here and never returned to gateway callers.
 */
type UniversalTreeMutationClass =
  | 'INSERT_MEMBER'
  | 'UPDATE_MEMBER'
  | 'DELETE_MEMBER'
  | 'ROLLBACK_CREATED_MEMBER'
  | 'RESTORE_MEMBER'
  | 'SET_PARENTS'
  | 'CONNECT_EXISTING_ANCESTRY_ATOMIC'
  | 'SET_SPOUSE';

type UniversalTreeInvocationResult =
  | {
      ok: true;
      result: Record<string, unknown>;
    }
  | {
      ok: false;
      reason: string;
      error?: unknown;
    };

const UNIVERSAL_TREE_FUNCTION_SLUG =
  'tree-universal-mutation-envelope';

const UNIVERSAL_TREE_ANCESTRY_MODES =
  new Set([
    'viewer-parent',
    'connector-parent',
    'co-parent-child',
    'self',
  ]);

const universalRecord = (
  value: unknown,
): value is Record<string, unknown> =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value);

const universalUuid = (
  value: unknown,
): value is string =>
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
    .test(value);

const universalUuidArray = (
  value: unknown,
): value is string[] =>
  Array.isArray(value) &&
  value.every(
    item =>
      universalUuid(item),
  );

const universalNullableUuid = (
  value: unknown,
): value is string | null =>
  value === null ||
  universalUuid(value);

const universalNullableString = (
  value: unknown,
): value is string | null =>
  value === null ||
  typeof value === 'string';

const universalNullableRecord = (
  value: unknown,
): value is Record<string, unknown> | null =>
  value === null ||
  universalRecord(value);

const universalExactKeys = (
  value: Record<string, unknown>,
  expected: readonly string[],
): boolean => {
  const actual =
    Object.keys(value).sort();

  const required =
    [...expected].sort();

  return (
    actual.length === required.length &&
    actual.every(
      (key, index) =>
        key === required[index],
    )
  );
};

const validateUniversalTreeBrowserResult = (
  mutationClass: UniversalTreeMutationClass,
  value: unknown,
): Record<string, unknown> | null => {
  if (!universalRecord(value)) {
    return null;
  }

  switch (mutationClass) {
    case 'INSERT_MEMBER':
    case 'UPDATE_MEMBER':
      return (
        universalExactKeys(
          value,
          ['memberId'],
        ) &&
        universalUuid(
          value.memberId,
        )
      )
        ? value
        : null;

    case 'DELETE_MEMBER':
      return (
        universalExactKeys(
          value,
          [
            'memberId',
            'status',
          ],
        ) &&
        universalUuid(
          value.memberId,
        ) &&
        value.status ===
          'deleted'
      )
        ? value
        : null;

    case 'ROLLBACK_CREATED_MEMBER':
      return (
        universalExactKeys(
          value,
          [
            'memberId',
            'status',
          ],
        ) &&
        universalUuid(
          value.memberId,
        ) &&
        value.status ===
          'rolled_back'
      )
        ? value
        : null;

    case 'RESTORE_MEMBER':
      return (
        universalExactKeys(
          value,
          [
            'memberId',
            'status',
          ],
        ) &&
        universalUuid(
          value.memberId,
        ) &&
        value.status ===
          'restored'
      )
        ? value
        : null;

    case 'SET_PARENTS':
      return (
        universalExactKeys(
          value,
          [
            'memberId',
            'parentIds',
          ],
        ) &&
        universalUuid(
          value.memberId,
        ) &&
        universalUuidArray(
          value.parentIds,
        )
      )
        ? value
        : null;

    case 'CONNECT_EXISTING_ANCESTRY_ATOMIC':
      return (
        universalExactKeys(
          value,
          [
            'status',
            'edgeMemberId',
            'evidenceMemberId',
            'parentIds',
            'relationshipWorkflow',
            'relationshipOverride',
            'auditId',
            'modeApplied',
          ],
        ) &&
        value.status ===
          'connected' &&
        universalUuid(
          value.edgeMemberId,
        ) &&
        universalUuid(
          value.evidenceMemberId,
        ) &&
        universalUuidArray(
          value.parentIds,
        ) &&
        universalNullableRecord(
          value.relationshipWorkflow,
        ) &&
        universalNullableString(
          value.relationshipOverride,
        ) &&
        universalUuid(
          value.auditId,
        ) &&
        typeof value.modeApplied ===
          'string' &&
        UNIVERSAL_TREE_ANCESTRY_MODES.has(
          value.modeApplied,
        )
      )
        ? value
        : null;

    case 'SET_SPOUSE':
      return (
        universalExactKeys(
          value,
          [
            'memberId',
            'spouseId',
            'verified',
          ],
        ) &&
        universalUuid(
          value.memberId,
        ) &&
        universalNullableUuid(
          value.spouseId,
        ) &&
        value.verified ===
          true
      )
        ? value
        : null;
  }
};

const validateUniversalTreeSuccessEnvelope = (
  mutationClass: UniversalTreeMutationClass,
  value: unknown,
): Record<string, unknown> | null => {
  if (
    !universalRecord(value) ||
    !universalExactKeys(
      value,
      [
        'ok',
        'milestone',
        'mutationClass',
        'mutationCorrelationId',
        'canonicalBeforeIntegrityComputed',
        'canonicalAfterIntegrityComputed',
        'canonicalRegressionComputed',
        'rawSnapshotsReturned',
        'evidencePersisted',
        'attestationIssued',
        'result',
      ],
    )
  ) {
    return null;
  }

  if (
    value.ok !== true ||
    typeof value.milestone !==
      'string' ||
    value.milestone.length === 0 ||
    value.mutationClass !==
      mutationClass ||
    !universalUuid(
      value.mutationCorrelationId,
    ) ||
    value.canonicalBeforeIntegrityComputed !==
      true ||
    value.canonicalAfterIntegrityComputed !==
      true ||
    value.canonicalRegressionComputed !==
      true ||
    value.rawSnapshotsReturned !==
      false ||
    value.evidencePersisted !==
      false ||
    value.attestationIssued !==
      false
  ) {
    return null;
  }

  return validateUniversalTreeBrowserResult(
    mutationClass,
    value.result,
  );
};

const universalTreeErrorMessage = (
  error: unknown,
): string =>
  error instanceof Error
    ? error.message
    : String(error);

const invokeUniversalTreeMutation = async (
  ctx: GatewayContext,
  mutationClass: UniversalTreeMutationClass,
  mutationPayload: Record<string, unknown>,
): Promise<UniversalTreeInvocationResult> => {
  try {
    const {
      data,
      error,
    } =
      await ctx.supabase
        .functions
        .invoke(
          UNIVERSAL_TREE_FUNCTION_SLUG,
          {
            body: {
              mutationClass,
              mutationPayload,
            },
          },
        );

    if (error) {
      return {
        ok: false,
        reason:
          'The governed universal Tree mutation envelope rejected the request.',
        error,
      };
    }

    const result =
      validateUniversalTreeSuccessEnvelope(
        mutationClass,
        data,
      );

    if (!result) {
      return {
        ok: false,
        reason:
          'The universal Tree Edge response failed browser contract validation.',
      };
    }

    return {
      ok: true,
      result,
    };
  } catch (error) {
    return {
      ok: false,
      reason:
        'Unexpected error while invoking the governed universal Tree mutation envelope: ' +
        universalTreeErrorMessage(error),
      error,
    };
  }
};

// =====================================================================
// 1. INSERT MEMBER
// =====================================================================

export interface GatewayInsertMemberInput {
  ownerUserId: string;
  name: string;
  relationship: GraphMember['relationship'];
  side: GraphMember['side'];
  /** All other columns map 1:1 to the existing addMember pipeline. */
  row: Record<string, any>;
  /** Duplicate-scan hints. */
  birthYear?: number | null;
  birthDate?: string | null;
  parentIds?: string[];
  spouseId?: string | null;
}

export async function gatewayInsertMember(
  ctx: GatewayContext,
  input: GatewayInsertMemberInput,
): Promise<
  GatewayResult<{
    id: string;
    row: any;
  }>
> {
  if (!ctx.actingUserId) {
    return block(
      'insert-member',
      ctx,
      null,
      null,
      BLOCKER.AUTH_REQUIRED,
      'Sign in required to add a family member.',
    );
  }

  const cleanName =
    (input.name ?? '').trim();

  if (cleanName.length < 1) {
    return block(
      'insert-member',
      ctx,
      null,
      null,
      BLOCKER.EMPTY_NAME,
      'A name is required.',
    );
  }

  if (
    Object.prototype.hasOwnProperty.call(
      input.row,
      'claimed_by_user_id',
    ) ||
    Object.prototype.hasOwnProperty.call(
      input.row,
      'claimed_at',
    ) ||
    input.row.profile_state ===
      'claimed'
  ) {
    return block(
      'insert-member',
      ctx,
      null,
      null,
      BLOCKER.TREE_MUTATION_BLOCKED,
      'Member creation cannot establish claim identity. Use a governed server transaction.',
    );
  }

  const constitutionRules =
    getKinsCircleConstitution();

  const constitutionText =
    JSON.stringify(
      constitutionRules,
    ).toLowerCase();

  const relationshipText =
    `${input.relationship ?? ''}`
      .toLowerCase();

  if (
    relationshipText.includes(
      'co-parent',
    ) &&
    !constitutionText.includes(
      'co-parent',
    )
  ) {
    return block(
      'insert-member',
      ctx,
      null,
      null,
      BLOCKER.WRONG_TREE_PARENT,
      'KinsCircle Constitution authority is missing co-parent rules; relationship mutation blocked.',
    );
  }

  if (
    (
      relationshipText.includes(
        'spouse',
      ) ||
      relationshipText.includes(
        'partner',
      )
    ) &&
    !constitutionText.includes(
      'spouse',
    )
  ) {
    return block(
      'insert-member',
      ctx,
      null,
      null,
      BLOCKER.PARENT_CHILD_AS_SPOUSE,
      'KinsCircle Constitution authority is missing spouse/partner rules; relationship mutation blocked.',
    );
  }

  if (
    input.parentIds &&
    input.spouseId &&
    input.parentIds.includes(
      input.spouseId,
    )
  ) {
    return block(
      'insert-member',
      ctx,
      null,
      null,
      BLOCKER.PARENT_CHILD_AS_SPOUSE,
      'A parent cannot also be the spouse of this member.',
    );
  }

  /*
   * INSERT_MEMBER is intentionally single-member only.
   *
   * Live relationship creation establishes partnerships separately through
   * governed SET_SPOUSE after the member exists. Never silently reinterpret
   * a direct spouse-bearing INSERT request.
   */
  if (
    input.spouseId !== undefined &&
    input.spouseId !== null
  ) {
    return block(
      'insert-member',
      ctx,
      null,
      null,
      BLOCKER.TREE_MUTATION_BLOCKED,
      'Member creation cannot establish spouse linkage. Create the member first, then use governed SET_SPOUSE.',
    );
  }

  if (!ctx.allowDuplicate) {
    const pre =
      runDuplicatePreflight({
        source:
          ctx.source ??
          'add-graph-member',

        name:
          cleanName,

        relationship:
          input.relationship,

        side:
          input.side,

        birthYear:
          input.birthYear ??
          null,

        birthDate:
          input.birthDate ??
          null,

        members:
          ctx.members,
      });

    if (
      pre.action ===
      'block'
    ) {
      return block(
        'insert-member',
        ctx,
        null,
        null,
        BLOCKER.DUPLICATE_BLOCKED,
        pre.reason ??
          'A possible existing family member matches this name.',
      );
    }
  }

  const integrity =
    validateGraphIntegrity(
      ctx.members,
    );

  if (
    integrity.severity ===
    'blocker'
  ) {
    return block(
      'insert-member',
      ctx,
      null,
      null,
      BLOCKER.CIRCULAR_ANCESTRY,
      'Graph integrity blocker detected. Repair the family tree before adding another member.',
    );
  }

  /*
   * Local-only noncanonical placeholder.
   *
   * It exists solely so the existing mutation authorization/preflight
   * interface can evaluate the proposed addition. It is never sent through
   * the universal envelope and can never become canonical identity.
   */
  const preflightId =
    uuidv4();

  const preflightRow = {
    ...input.row,
    id:
      preflightId,
  };

  const mandatoryBlock =
    authorizeProposedMutation(
      'insert-member',
      ctx,
      preflightId,
      null,
      () =>
        buildInsertProposal({
          id:
            preflightId,

          row:
            preflightRow,

          fallbackParentIds:
            input.parentIds ??
            [],

          fallbackSpouseId:
            null,
        }),
    );

  if (mandatoryBlock) {
    return mandatoryBlock;
  }

  /*
   * J4S exact INSERT row allowlist.
   *
   * Canonical identity, ownership, creator, claim state, timestamps,
   * structural parent/spouse fields and confirmations are deliberately not
   * transported inside row.
   */
  const allowedRowKeys =
    [
      'nickname',
      'photo_url',
      'birth_month',
      'birth_day',
      'birthday_visibility',
      'birthday_reminders',
      'living_status',
      'notes',
      'city',
      'description',
      'approval_status',
      'relationship_override',
      'metadata',
    ] as const;

  const sanitizedRow:
    Record<string, unknown> =
      {};

  for (
    const key
    of allowedRowKeys
  ) {
    if (
      Object.prototype
        .hasOwnProperty
        .call(
          input.row,
          key,
        )
    ) {
      sanitizedRow[key] =
        input.row[key];
    }
  }

  const parentIds =
    Array.from(
      new Set(
        (
          input.parentIds ??
          []
        ).filter(Boolean),
      ),
    );

  try {
    const universal =
      await invokeUniversalTreeMutation(
        ctx,
        'INSERT_MEMBER',
        {
          name:
            cleanName,

          relationship:
            input.relationship,

          side:
            input.side,

          row:
            sanitizedRow,

          birthYear:
            input.birthYear ??
            null,

          birthDate:
            input.birthDate ??
            null,

          parentIds,

          spouseId:
            null,

          allowDuplicate:
            Boolean(
              ctx.allowDuplicate,
            ),
        },
      );

    if (universal.ok === false) {
      return fail(
        'insert-member',
        ctx,
        null,
        null,
        universal.reason,
        universal.error,
      );
    }

    const serverMemberId =
      universal.result
        .memberId;

    if (
      typeof serverMemberId !==
        'string' ||
      !serverMemberId
    ) {
      return fail(
        'insert-member',
        ctx,
        null,
        null,
        'Universal INSERT_MEMBER did not return the committed canonical member id.',
      );
    }

    /*
     * Read-only hydration by the exact committed server-generated ID.
     *
     * There is deliberately no name/time fallback and no INSERT retry if
     * hydration fails.
     */
    const {
      data:
        hydratedRow,
      error:
        hydrationError,
    } =
      await ctx.supabase
        .from(
          'family_graph_members',
        )
        .select('*')
        .eq(
          'id',
          serverMemberId,
        )
        .is(
          'deleted_at',
          null,
        )
        .maybeSingle();

    if (
      hydrationError ||
      !hydratedRow
    ) {
      return fail(
        'insert-member',
        ctx,
        serverMemberId,
        null,
        'Member creation committed, but read-only hydration by the committed member id failed. The insert was not retried.',
        hydrationError ??
          undefined,
      );
    }

    if (
      hydratedRow.id !==
      serverMemberId
    ) {
      return fail(
        'insert-member',
        ctx,
        serverMemberId,
        null,
        'Read-only INSERT_MEMBER hydration returned a row with the wrong canonical member id.',
      );
    }

    return allow(
      'insert-member',
      ctx,
      serverMemberId,
      null,
      snapshotRow(
        hydratedRow,
      ),
      {
        id:
          serverMemberId,

        row:
          hydratedRow,
      },
    );
  } catch (error) {
    return fail(
      'insert-member',
      ctx,
      null,
      null,
      'Unexpected error during universal member creation.',
      error,
    );
  }
}

// =====================================================================
// 2. UPDATE MEMBER (generic field updates — NOT parent/spouse/claim)
// =====================================================================

export interface GatewayUpdateMemberInput {
  id: string;
  /** Already-mapped supabase column row (e.g. { photo_url: ..., name: ... }). */
  row: Record<string, any>;
  /** When the row touches `parent_ids`, callers MUST use gatewaySetParents
   *  instead — this function refuses parent_ids edits. */
  /** Same for spouse_id (use gatewaySetSpouse) and claimed_by_user_id
   *  (claim identity requires governed server transaction authority). */
}

export async function gatewayUpdateMember(
  ctx: GatewayContext,
  input: GatewayUpdateMemberInput,
): Promise<GatewayResult<{ id: string }>> {
  if (!ctx.actingUserId) {
    return block('update-member', ctx, input.id, null, BLOCKER.AUTH_REQUIRED,
      'Sign in required to edit a family member.');
  }
  // Reject the relationship-affecting fields here. Callers must use the
  // dedicated entry points so the right validations run.
  if (Object.prototype.hasOwnProperty.call(input.row, 'parent_ids')) {
    return block('update-member', ctx, input.id, null, BLOCKER.WRONG_TREE_PARENT,
      'Parent edits must go through gatewaySetParents.');
  }
  if (Object.prototype.hasOwnProperty.call(input.row, 'spouse_id')) {
    return block('update-member', ctx, input.id, null, BLOCKER.PARENT_CHILD_AS_SPOUSE,
      'Spouse edits must go through gatewaySetSpouse.');
  }
  if (Object.prototype.hasOwnProperty.call(input.row, 'claimed_by_user_id')) {
    return block('update-member', ctx, input.id, null, BLOCKER.ALREADY_CLAIMED_OTHER,
      'Claim identity cannot be changed through generic browser mutation.');
  }
  const before = snapshotMember(
    ctx.members.find((m) => m.id === input.id),
  );

  const mandatoryBlock =
    authorizeProposedMutation(
      'update-member',
      ctx,
      input.id,
      before,
      () =>
        buildGenericUpdateProposal(
          input.id,
          input.row,
        ),
    );

  if (mandatoryBlock) {
    return mandatoryBlock;
  }

  try {
    const invocation =
      await invokeUniversalTreeMutation(
        ctx,
        'UPDATE_MEMBER',
        {
          memberId:
            input.id,
          row:
            input.row,
        },
      );

    if (invocation.ok === false) {
      return fail(
        'update-member',
        ctx,
        input.id,
        before,
        invocation.reason,
        invocation.error,
      );
    }

    const memberId =
      invocation.result.memberId;

    if (
      typeof memberId !== 'string' ||
      memberId !== input.id
    ) {
      return fail(
        'update-member',
        ctx,
        input.id,
        before,
        'Universal Tree mutation returned an unexpected member identity.',
      );
    }

    return allow(
      'update-member',
      ctx,
      memberId,
      before,
      {
        ...(before ?? {}),
        ...input.row,
      },
      {
        id:
          memberId,
      },
    );
  } catch (err) {
    return fail(
      'update-member',
      ctx,
      input.id,
      before,
      'Unexpected error during universal update mutation.',
      err,
    );
  }
}

// =====================================================================
// 3. DELETE MEMBER
// =====================================================================

export interface GatewayDeleteMemberRequest {
  readonly memberId: string;
  readonly errorClass: Exclude<
    CanonicalTreeDeletionErrorClass,
    'FAILED_CREATION'
  >;
  readonly explanation: string;
}

export interface GatewayRollbackCreatedMemberRequest {
  readonly memberId: string;
  readonly errorClass: 'FAILED_CREATION';
  readonly explanation: string;
}

interface GatewayDeletionEvidenceResult {
  readonly ok: boolean;
  readonly evidence:
    CanonicalTreeDeletionDependencyEvidence | null;
  readonly reason: string | null;
}

const loadGatewayDeletionDependencyEvidence = async (
  ctx: GatewayContext,
  memberId: string,
): Promise<GatewayDeletionEvidenceResult> => {
  const countQuery = async (
    label: string,
    query: PromiseLike<{
      count: number | null;
      error: { message?: string } | null;
    }>,
  ): Promise<number> => {
    const result = await query;

    if (result.error) {
      throw new Error(
        `${label}: ${
          result.error.message ??
          'dependency query failed'
        }`,
      );
    }

    if (
      typeof result.count !== 'number' ||
      !Number.isFinite(result.count)
    ) {
      throw new Error(
        `${label}: authoritative count unavailable`,
      );
    }

    return Math.max(
      0,
      Math.floor(result.count),
    );
  };

  try {
    const [
      memoryCount,
      commentCount,
      directPhotoCount,
      taggedPhotoCount,
      sharedPhotoCount,
      confirmationCount,
      sourceMergeCount,
      targetMergeCount,
      familyUnitCount,
    ] = await Promise.all([
      countQuery(
        'family_graph_memories',
        ctx.supabase
          .from('family_graph_memories')
          .select('id', {
            count: 'exact',
            head: true,
          })
          .eq('member_id', memberId),
      ),
      countQuery(
        'family_graph_memory_comments',
        ctx.supabase
          .from('family_graph_memory_comments')
          .select('id', {
            count: 'exact',
            head: true,
          })
          .eq('member_id', memberId),
      ),
      countQuery(
        'family_graph_photos.member_id',
        ctx.supabase
          .from('family_graph_photos')
          .select('id', {
            count: 'exact',
            head: true,
          })
          .eq('member_id', memberId),
      ),
      countQuery(
        'family_graph_photos.tagged_member_ids',
        ctx.supabase
          .from('family_graph_photos')
          .select('id', {
            count: 'exact',
            head: true,
          })
          .contains(
            'tagged_member_ids',
            [memberId],
          ),
      ),
      countQuery(
        'family_graph_photos.shared_with_member_ids',
        ctx.supabase
          .from('family_graph_photos')
          .select('id', {
            count: 'exact',
            head: true,
          })
          .contains(
            'shared_with_member_ids',
            [memberId],
          ),
      ),
      countQuery(
        'family_graph_confirmations',
        ctx.supabase
          .from('family_graph_confirmations')
          .select('id', {
            count: 'exact',
            head: true,
          })
          .eq('member_id', memberId),
      ),
      countQuery(
        'family_graph_merge_requests.source',
        ctx.supabase
          .from('family_graph_merge_requests')
          .select('id', {
            count: 'exact',
            head: true,
          })
          .eq('source_member_id', memberId),
      ),
      countQuery(
        'family_graph_merge_requests.target',
        ctx.supabase
          .from('family_graph_merge_requests')
          .select('id', {
            count: 'exact',
            head: true,
          })
          .eq('target_member_id', memberId),
      ),
      countQuery(
        'family_units',
        ctx.supabase
          .from('family_units')
          .select('id', {
            count: 'exact',
            head: true,
          })
          .contains(
            'member_ids',
            [memberId],
          ),
      ),
    ]);

    const target =
      ctx.members.find(
        member => member.id === memberId,
      ) ?? null;

    const claimHistoryCount =
      target &&
      (
        target.profileState === 'claimed' ||
        Boolean(target.claimedByUserId) ||
        Boolean(target.claimedAt)
      )
        ? 1
        : 0;

    return {
      ok: true,
      evidence: {
        mediaCount:
          directPhotoCount +
          taggedPhotoCount +
          sharedPhotoCount,
        memoryCount,
        invitationCount: 0,
        claimHistoryCount,
        externalDependencyCount:
          commentCount +
          confirmationCount +
          sourceMergeCount +
          targetMergeCount +
          familyUnitCount,
      },
      reason: null,
    };
  } catch (error) {
    return {
      ok: false,
      evidence: null,
      reason:
        error instanceof Error
          ? error.message
          : 'Required deletion dependency evidence is unavailable.',
    };
  }
};

const executeGatewayDeleteMember = async (
  ctx: GatewayContext,
  request:
    | GatewayDeleteMemberRequest
    | GatewayRollbackCreatedMemberRequest,
): Promise<
  GatewayResult<{
    id: string;
    status: string;
  }>
> => {
  const id = request.memberId;

  if (!ctx.actingUserId) {
    return block(
      'delete-member',
      ctx,
      id,
      null,
      BLOCKER.AUTH_REQUIRED,
      'Sign in required to remove an erroneous family record.',
    );
  }

  const beforeMember =
    ctx.members.find(
      member => member.id === id,
    ) ?? null;

  const before =
    snapshotMember(beforeMember);

  if (!beforeMember) {
    return block(
      'delete-member',
      ctx,
      id,
      null,
      BLOCKER.ROW_NOT_FOUND,
      'The selected active family record was not found.',
    );
  }

  const explanation =
    request.explanation.trim();

  if (!explanation) {
    return block(
      'delete-member',
      ctx,
      id,
      before,
      BLOCKER.DELETION_BLOCKED,
      'A human-readable explanation is required.',
    );
  }

  const mandatoryBlock =
    authorizeProposedMutation(
      'delete-member',
      ctx,
      id,
      before,
      {
        kind: 'delete-member',
        memberId: id,
      },
    );

  if (mandatoryBlock) {
    return mandatoryBlock;
  }

  const evidenceResult =
    await loadGatewayDeletionDependencyEvidence(
      ctx,
      id,
    );

  if (
    !evidenceResult.ok ||
    !evidenceResult.evidence
  ) {
    return block(
      'delete-member',
      ctx,
      id,
      before,
      BLOCKER.DEPENDENCY_EVIDENCE_UNAVAILABLE,
      [
        'Deletion dependency evidence could not be certified.',
        evidenceResult.reason ??
          'Required evidence was unavailable.',
      ].join(' '),
    );
  }

  const decision =
    authorizeCanonicalTreeErrorOnlyDeletion({
      members: ctx.members,
      memberId: id,
      viewerMemberId:
        ctx.viewerMemberId,
      errorClass:
        request.errorClass,
      dependencies:
        evidenceResult.evidence,
    });

  if (!decision.authorized) {
    return block(
      'delete-member',
      ctx,
      id,
      before,
      BLOCKER.DELETION_BLOCKED,
      [
        decision.reason,
        `Blockers: ${
          decision.blockers.join(', ') ||
          'unspecified'
        }.`,
      ].join(' '),
    );
  }

  const mutationClass:
    | 'DELETE_MEMBER'
    | 'ROLLBACK_CREATED_MEMBER' =
      request.errorClass ===
        'FAILED_CREATION'
        ? 'ROLLBACK_CREATED_MEMBER'
        : 'DELETE_MEMBER';

  const expectedStatus =
    mutationClass ===
      'ROLLBACK_CREATED_MEMBER'
      ? 'rolled_back'
      : 'deleted';

  try {
    const invocation =
      await invokeUniversalTreeMutation(
        ctx,
        mutationClass,
        {
          memberId:
            id,
          errorClass:
            request.errorClass,
          explanation,
        },
      );

    if (invocation.ok === false) {
      return fail(
        'delete-member',
        ctx,
        id,
        before,
        invocation.reason,
        invocation.error,
      );
    }

    const memberId =
      invocation.result.memberId;

    const resultStatus =
      invocation.result.status;

    if (
      typeof memberId !== 'string' ||
      memberId !== id ||
      resultStatus !== expectedStatus
    ) {
      return fail(
        'delete-member',
        ctx,
        id,
        before,
        'Universal Tree mutation returned an invalid delete or rollback result.',
      );
    }

    return allow(
      'delete-member',
      ctx,
      memberId,
      before,
      null,
      {
        id:
          memberId,
        status:
          expectedStatus,
      },
    );
  } catch (error) {
    return fail(
      'delete-member',
      ctx,
      id,
      before,
      'Unexpected error during universal delete or rollback mutation.',
      error,
    );
  }
};

export async function gatewayDeleteMember(
  ctx: GatewayContext,
  request: GatewayDeleteMemberRequest,
): Promise<
  GatewayResult<{
    id: string;
    status: string;
  }>
> {
  if (
    request.errorClass ===
    ('FAILED_CREATION' as CanonicalTreeDeletionErrorClass)
  ) {
    return block(
      'delete-member',
      ctx,
      request.memberId,
      snapshotMember(
        ctx.members.find(
          member =>
            member.id === request.memberId,
        ),
      ),
      BLOCKER.DELETION_BLOCKED,
      'FAILED_CREATION is restricted to governed add-relative rollback.',
    );
  }

  return executeGatewayDeleteMember(
    ctx,
    request,
  );
}

export async function gatewayRollbackCreatedMember(
  ctx: GatewayContext,
  request: GatewayRollbackCreatedMemberRequest,
): Promise<
  GatewayResult<{
    id: string;
    status: string;
  }>
> {
  const target =
    ctx.members.find(
      member =>
        member.id === request.memberId,
    ) ?? null;

  if (
    ctx.source !==
    'add-relative-rollback'
  ) {
    return block(
      'delete-member',
      ctx,
      request.memberId,
      snapshotMember(target),
      BLOCKER.DELETION_BLOCKED,
      'FAILED_CREATION rollback requires the controlled add-relative workflow.',
    );
  }

  if (
    !target ||
    target.createdBy !==
      ctx.actingUserId
  ) {
    return block(
      'delete-member',
      ctx,
      request.memberId,
      snapshotMember(target),
      BLOCKER.DELETION_BLOCKED,
      'Rollback is restricted to a row created by the current add-relative actor.',
    );
  }

  const createdAt =
    Date.parse(target.createdAt);

  if (
    !Number.isFinite(createdAt) ||
    Date.now() - createdAt >
      15 * 60 * 1000
  ) {
    return block(
      'delete-member',
      ctx,
      request.memberId,
      snapshotMember(target),
      BLOCKER.DELETION_BLOCKED,
      'Rollback is restricted to a newly created add-relative row.',
    );
  }

  return executeGatewayDeleteMember(
    ctx,
    request,
  );
}

export async function gatewayRestoreMember(
  ctx: GatewayContext,
  id: string,
): Promise<GatewayResult<{ id: string; status: string }>> {
  if (!ctx.actingUserId) {
    return block(
      'restore-member',
      ctx,
      id,
      null,
      BLOCKER.AUTH_REQUIRED,
      'Sign in required to restore a family member.',
    );
  }

  const before = snapshotMember(
    ctx.members.find((m) => m.id === id),
  );

  let restoreRow: Record<string, any> | null =
    null;

  try {
    const {
      data,
      error,
    } = await ctx.supabase
      .from('family_graph_members')
      .select(
        [
          'id',
          'parent_ids',
          'spouse_id',
          'claimed_by_user_id',
          'profile_state',
          'approval_status',
          'deleted_at',
          'deletion_snapshot',
        ].join(','),
      )
      .eq('id', id)
      .not('deleted_at', 'is', null)
      .maybeSingle();

    if (error) {
      return fail(
        'restore-member',
        ctx,
        id,
        before,
        'Database refused the restoration evidence read.',
        error,
      );
    }

    restoreRow = data;
  } catch (error) {
    return fail(
      'restore-member',
      ctx,
      id,
      before,
      'Unexpected error while reading restoration evidence.',
      error,
    );
  }

  if (!restoreRow) {
    return block(
      'restore-member',
      ctx,
      id,
      before,
      BLOCKER.ROW_NOT_FOUND,
      'Deleted profile and restoration evidence were not found.',
    );
  }

  const deletionSnapshot =
    restoreRow.deletion_snapshot &&
    typeof restoreRow.deletion_snapshot ===
      'object'
      ? restoreRow.deletion_snapshot
      : {};

  const childIds = Array.isArray(
    deletionSnapshot.child_ids,
  )
    ? deletionSnapshot.child_ids.filter(
        (value: unknown): value is string =>
          typeof value === 'string',
      )
    : [];

  const spouseReferenceIds = Array.isArray(
    deletionSnapshot.spouse_reference_ids,
  )
    ? deletionSnapshot.spouse_reference_ids.filter(
        (value: unknown): value is string =>
          typeof value === 'string',
      )
    : [];

  const mandatoryBlock =
    authorizeProposedMutation(
      'restore-member',
      ctx,
      id,
      before,
      () =>
        buildRestoreProposal({
          member:
            mapDatabaseRowToMandatoryMember(
              id,
              restoreRow!,
            ),
          childIds,
          spouseReferenceIds,
        }),
      'integrity-improvement',
    );

  if (mandatoryBlock) {
    return mandatoryBlock;
  }

  try {
    const invocation =
      await invokeUniversalTreeMutation(
        ctx,
        'RESTORE_MEMBER',
        {
          memberId:
            id,
        },
      );

    if (invocation.ok === false) {
      return fail(
        'restore-member',
        ctx,
        id,
        before,
        invocation.reason,
        invocation.error,
      );
    }

    const memberId =
      invocation.result.memberId;

    const status =
      invocation.result.status;

    if (
      typeof memberId !== 'string' ||
      memberId !== id ||
      status !== 'restored'
    ) {
      return fail(
        'restore-member',
        ctx,
        id,
        before,
        'Universal Tree mutation returned an invalid RESTORE_MEMBER result.',
      );
    }

    return allow(
      'restore-member',
      ctx,
      memberId,
      before,
      before,
      {
        id:
          memberId,
        status,
      },
    );
  } catch (err) {
    return fail(
      'restore-member',
      ctx,
      id,
      before,
      'Unexpected error during universal restore mutation.',
      err,
    );
  }
}

// =====================================================================
// 4. SET PARENTS
// =====================================================================

export async function gatewaySetParents(
  ctx: GatewayContext,
  memberId: string,
  parentIds: string[],
): Promise<GatewayResult<{ id: string; parentIds: string[] }>> {
  if (!ctx.actingUserId) {
    return block('set-parents', ctx, memberId, null, BLOCKER.AUTH_REQUIRED,
      'Sign in required.');
  }
  const target = ctx.members.find((m) => m.id === memberId) ?? null;
  const before = snapshotMember(target);

  // De-dup + drop self.
  const cleanIds = Array.from(new Set(parentIds))
    .filter((pid) => !!pid && pid !== memberId);

  // Self-as-parent.
  if (cleanIds.length !== Array.from(new Set(parentIds)).filter(Boolean).length) {
    return block('set-parents', ctx, memberId, before, BLOCKER.SELF_AS_PARENT,
      'A member cannot be their own parent.');
  }

  // Wrong-tree: every candidate parent must share the member's owner_user_id.
  if (target) {
    for (const pid of cleanIds) {
      const cand = ctx.members.find((m) => m.id === pid);
      if (cand && cand.ownerUserId !== target.ownerUserId) {
        return block('set-parents', ctx, memberId, before, BLOCKER.WRONG_TREE_PARENT,
          'Parents must live on the same family tree.');
      }
    }
  }

  // Spouse-as-parent overlap.
  const spouseOfTarget = target?.spouseId ?? null;
  if (spouseOfTarget && cleanIds.includes(spouseOfTarget)) {
    return block('set-parents', ctx, memberId, before, BLOCKER.PARENT_CHILD_AS_SPOUSE,
      "A member's own spouse cannot be one of their parents.");
  }

  // Cycle guard — parent's ancestors must not include memberId.
  const byId = new Map(ctx.members.map((m) => [m.id, m]));
  const ancestorsOf = (id: string): Set<string> => {
    const seen = new Set<string>();
    const stack = [id];
    let steps = 0;
    while (stack.length && steps < 5000) {
      steps += 1;
      const cur = stack.pop()!;
      const m = byId.get(cur);
      if (!m) continue;
      for (const pid of m.parentIds ?? []) {
        if (!seen.has(pid)) {
          seen.add(pid);
          stack.push(pid);
        }
      }
    }
    return seen;
  };
  for (const pid of cleanIds) {
    if (ancestorsOf(pid).has(memberId)) {
      return block('set-parents', ctx, memberId, before, BLOCKER.CIRCULAR_ANCESTRY,
        'That edge would create a circular ancestry loop.');
    }
  }

  const mandatoryBlock =
    authorizeProposedMutation(
      'set-parents',
      ctx,
      memberId,
      before,
      {
        kind: 'set-parents',
        memberId,
        parentIds: cleanIds,
      },
    );

  if (mandatoryBlock) {
    return mandatoryBlock;
  }

  try {
    const invocation =
      await invokeUniversalTreeMutation(
        ctx,
        'SET_PARENTS',
        {
          memberId,
          parentIds:
            cleanIds,
        },
      );

    if (invocation.ok === false) {
      return fail(
        'set-parents',
        ctx,
        memberId,
        before,
        invocation.reason,
        invocation.error,
      );
    }

    const resultMemberId =
      invocation.result.memberId;

    const resultParentIds =
      invocation.result.parentIds;

    if (
      typeof resultMemberId !== 'string' ||
      resultMemberId !== memberId ||
      !Array.isArray(resultParentIds) ||
      !resultParentIds.every(
        value =>
          typeof value === 'string',
      )
    ) {
      return fail(
        'set-parents',
        ctx,
        memberId,
        before,
        'Universal Tree mutation returned an invalid SET_PARENTS result.',
      );
    }

    const normalizedResultParentIds =
      [...resultParentIds].sort();

    const normalizedRequestedParentIds =
      [...cleanIds].sort();

    if (
      normalizedResultParentIds.length !==
        normalizedRequestedParentIds.length ||
      !normalizedResultParentIds.every(
        (value, index) =>
          value ===
            normalizedRequestedParentIds[index],
      )
    ) {
      return fail(
        'set-parents',
        ctx,
        memberId,
        before,
        'Universal Tree mutation returned unexpected canonical parent ids.',
      );
    }

    return allow(
      'set-parents',
      ctx,
      resultMemberId,
      before,
      {
        ...(before ?? {}),
        parentIds:
          resultParentIds,
      },
      {
        id:
          resultMemberId,
        parentIds:
          resultParentIds,
      },
    );
  } catch (err) {
    return fail(
      'set-parents',
      ctx,
      memberId,
      before,
      'Unexpected error during universal set-parents mutation.',
      err,
    );
  }
}


export interface GatewayConnectExistingAncestryRequest {
  readonly viewerMemberId: string;
  readonly relationship: Relationship;
  readonly requestedSide: Side | null;
  readonly anchorMemberId: string | null;
  readonly secondAnchorMemberId: string | null;

  readonly edgeMemberId: string;
  readonly evidenceMemberId: string;

  readonly expectedParentIds:
    readonly string[];

  readonly proposedParentIds:
    readonly string[];

  readonly expectedRelationshipWorkflow:
    TreeRelationshipWorkflow | null;

  readonly relationshipWorkflow:
    TreeRelationshipWorkflow | null;

  readonly expectedRelationshipOverride:
    string | null;

  readonly relationshipOverride:
    string | null;

  readonly relationshipKey: string;

  readonly modeApplied:
    | 'viewer-parent'
    | 'connector-parent'
    | 'co-parent-child'
    | 'self';
}

export interface GatewayConnectExistingAncestryResult {
  readonly status: string;
  readonly edgeMemberId: string;
  readonly evidenceMemberId: string;
  readonly parentIds: string[];

  readonly relationshipWorkflow:
    TreeRelationshipWorkflow | null;

  readonly relationshipOverride:
    string | null;

  readonly auditId: string;
}

export async function gatewayConnectExistingAncestryAtomic(
  ctx: GatewayContext,
  request: GatewayConnectExistingAncestryRequest,
): Promise<
  GatewayResult<
    GatewayConnectExistingAncestryResult
  >
> {
  const beforeMember =
    ctx.members.find(
      member =>
        member.id ===
        request.edgeMemberId,
    ) ?? null;

  const before =
    snapshotMember(
      beforeMember,
    );

  if (!ctx.actingUserId) {
    return block(
      'set-parents',
      ctx,
      request.edgeMemberId,
      before,
      BLOCKER.AUTH_REQUIRED,
      'Sign in required for atomic ancestry connection.',
    );
  }

  if (
    !ctx.viewerMemberId ||
    request.viewerMemberId !==
      ctx.viewerMemberId
  ) {
    return block(
      'set-parents',
      ctx,
      request.edgeMemberId,
      before,
      BLOCKER.TREE_MUTATION_BLOCKED,
      'Viewer identity authority does not match the loaded Tree context.',
    );
  }

  const evidenceMember =
    ctx.members.find(
      member =>
        member.id ===
        request.evidenceMemberId,
    ) ?? null;

  if (
    !beforeMember ||
    !evidenceMember
  ) {
    return block(
      'set-parents',
      ctx,
      request.edgeMemberId,
      before,
      BLOCKER.ROW_NOT_FOUND,
      'Atomic ancestry edge or evidence member is absent from the loaded graph.',
    );
  }

  if (
    beforeMember.ownerUserId !==
      evidenceMember.ownerUserId
  ) {
    return block(
      'set-parents',
      ctx,
      request.edgeMemberId,
      before,
      BLOCKER.WRONG_TREE_PARENT,
      'Atomic ancestry edge and evidence endpoints must share one family graph.',
    );
  }

  const canWrite = (
    member: GraphMember,
  ): boolean =>
    member.claimedByUserId
      ? member.claimedByUserId ===
          ctx.actingUserId
      : member.ownerUserId ===
          ctx.actingUserId;

  if (
    !canWrite(beforeMember) ||
    !canWrite(evidenceMember)
  ) {
    return block(
      'set-parents',
      ctx,
      request.edgeMemberId,
      before,
      BLOCKER.TREE_MUTATION_BLOCKED,
      'Atomic ancestry transaction does not have owner-or-claimed write authority for every mutated row.',
    );
  }

  const isCoparentChild =
    request.relationship ===
      'co-parent' ||
    request.modeApplied ===
      'co-parent-child';

  if (isCoparentChild) {
    if (
      request.relationship !==
        'co-parent' ||
      request.modeApplied !==
        'co-parent-child' ||
      ![
        'co-parent',
        'v_coparent',
      ].includes(
        request.relationshipKey,
      ) ||
      !request.relationshipWorkflow ||
      request.relationshipWorkflow.kind !==
        'co-parent' ||
      request.relationshipWorkflow
        .createsAncestry !== false ||
      request.relationshipWorkflow
        .inheritsGeneration !== true ||
      request.relationshipWorkflow
        .inheritsBranch !== true ||
      request.relationshipWorkflow
        .partnershipStatus !==
          'coparent-only' ||
      request.relationshipWorkflow
        .connectorMemberId !==
          request.edgeMemberId
    ) {
      return block(
        'set-parents',
        ctx,
        request.edgeMemberId,
        before,
        BLOCKER.TREE_MUTATION_BLOCKED,
        'Atomic co-parent-child authority requires exact child-mediated workflow evidence.',
      );
    }
  } else if (
    !request.relationshipWorkflow ||
    request.relationshipWorkflow.kind !==
      'structural' ||
    request.relationshipWorkflow
      .createsAncestry !==
      true
  ) {
    return block(
      'set-parents',
      ctx,
      request.edgeMemberId,
      before,
      BLOCKER.TREE_MUTATION_BLOCKED,
      'Atomic ancestry RPC accepts structural ancestry workflows only.',
    );
  }

  const allowedRelationships =
    new Set<string>([
      'mother',
      'father',
      'adopted-parent',
      'sibling',
      'child',
      'grandparent',
      'great-grandparent',
      'aunt-uncle',
      'cousin',
      'cousin-child',
      'co-parent',
    ]);

  if (
    !allowedRelationships.has(
      String(
        request.relationship,
      ),
    )
  ) {
    return block(
      'set-parents',
      ctx,
      request.edgeMemberId,
      before,
      BLOCKER.TREE_MUTATION_BLOCKED,
      'Relationship class is not eligible for the atomic ancestry writer.',
    );
  }

  const expectedMode =
    request.relationship ===
      'co-parent'
      ? 'co-parent-child'
      : request.relationship ===
          'mother' ||
        request.relationship ===
          'father' ||
        request.relationship ===
          'adopted-parent'
        ? 'viewer-parent'
        : request.relationship ===
            'grandparent' ||
          request.relationship ===
            'great-grandparent'
          ? 'connector-parent'
          : 'self';

  if (
    request.modeApplied !==
    expectedMode
  ) {
    return block(
      'set-parents',
      ctx,
      request.edgeMemberId,
      before,
      BLOCKER.TREE_MUTATION_BLOCKED,
      'Atomic ancestry mode does not match the structural relationship class.',
    );
  }

  if (
    request.modeApplied ===
      'viewer-parent' &&
    request.edgeMemberId !==
      request.viewerMemberId
  ) {
    return block(
      'set-parents',
      ctx,
      request.edgeMemberId,
      before,
      BLOCKER.TREE_MUTATION_BLOCKED,
      'Viewer-parent ancestry must mutate the authenticated viewer endpoint.',
    );
  }

  if (
    request.modeApplied ===
      'connector-parent' &&
    request.anchorMemberId !==
      request.edgeMemberId
  ) {
    return block(
      'set-parents',
      ctx,
      request.edgeMemberId,
      before,
      BLOCKER.TREE_MUTATION_BLOCKED,
      'Connector-parent ancestry must mutate the governed connector endpoint.',
    );
  }

  if (
    request.modeApplied ===
      'self' &&
    request.edgeMemberId !==
      request.evidenceMemberId
  ) {
    return block(
      'set-parents',
      ctx,
      request.edgeMemberId,
      before,
      BLOCKER.TREE_MUTATION_BLOCKED,
      'Self-mode ancestry must mutate the existing profile being connected.',
    );
  }

  if (
    request.modeApplied ===
      'co-parent-child' &&
    (
      request.anchorMemberId !==
        request.edgeMemberId ||
      request.edgeMemberId ===
        request.evidenceMemberId
    )
  ) {
    return block(
      'set-parents',
      ctx,
      request.edgeMemberId,
      before,
      BLOCKER.TREE_MUTATION_BLOCKED,
      'Co-parent-child authority must mutate the explicitly selected shared child.',
    );
  }

  const normalize = (
    values:
      readonly string[],
  ): string[] =>
    Array.from(
      new Set(
        values.filter(Boolean),
      ),
    ).sort();

  const expected =
    normalize(
      request.expectedParentIds,
    );

  const proposed =
    normalize(
      request.proposedParentIds,
    );

  const current =
    normalize(
      beforeMember.parentIds ??
      [],
    );

  const same = (
    left: readonly string[],
    right: readonly string[],
  ): boolean =>
    left.length ===
      right.length &&
    left.every(
      (value, index) =>
        value === right[index],
    );

  if (!same(expected, current)) {
    return block(
      'set-parents',
      ctx,
      request.edgeMemberId,
      before,
      BLOCKER.TREE_MUTATION_BLOCKED,
      'Loaded parent state is already stale before the atomic RPC call.',
    );
  }

  if (
    expected.some(
      parentId =>
        !proposed.includes(
          parentId,
        ),
    )
  ) {
    return block(
      'set-parents',
      ctx,
      request.edgeMemberId,
      before,
      BLOCKER.TREE_MUTATION_BLOCKED,
      'Atomic ancestry proposals are additive-only and may not remove an existing parent reference.',
    );
  }

  if (
    proposed.includes(
      request.edgeMemberId,
    )
  ) {
    return block(
      'set-parents',
      ctx,
      request.edgeMemberId,
      before,
      BLOCKER.SELF_AS_PARENT,
      'A member cannot be their own parent.',
    );
  }

  if (
    beforeMember.spouseId &&
    proposed.includes(
      beforeMember.spouseId,
    )
  ) {
    return block(
      'set-parents',
      ctx,
      request.edgeMemberId,
      before,
      BLOCKER.PARENT_CHILD_AS_SPOUSE,
      "A member's spouse cannot also be their parent.",
    );
  }

  const additions =
    proposed.filter(
      parentId =>
        !expected.includes(
          parentId,
        ),
    );

  for (const parentId of additions) {
    const parent =
      ctx.members.find(
        member =>
          member.id ===
          parentId,
      );

    if (
      !parent ||
      parent.ownerUserId !==
        beforeMember.ownerUserId
    ) {
      return block(
        'set-parents',
        ctx,
        request.edgeMemberId,
        before,
        BLOCKER.WRONG_TREE_PARENT,
        'Every newly-added ancestry endpoint must be an active member of the same family graph.',
      );
    }
  }

  const byId =
    new Map(
      ctx.members.map(
        member => [
          member.id,
          member,
        ],
      ),
    );

  const ancestorsOf = (
    memberId: string,
  ): Set<string> => {
    const seen =
      new Set<string>();

    const stack = [
      memberId,
    ];

    while (stack.length) {
      const currentId =
        stack.pop()!;

      const member =
        byId.get(
          currentId,
        );

      if (!member) {
        continue;
      }

      for (
        const parentId
        of member.parentIds ??
          []
      ) {
        if (
          seen.has(
            parentId,
          )
        ) {
          continue;
        }

        seen.add(
          parentId,
        );

        stack.push(
          parentId,
        );
      }
    }

    return seen;
  };

  for (const parentId of proposed) {
    if (
      ancestorsOf(
        parentId,
      ).has(
        request.edgeMemberId,
      )
    ) {
      return block(
        'set-parents',
        ctx,
        request.edgeMemberId,
        before,
        BLOCKER.CIRCULAR_ANCESTRY,
        'Atomic ancestry proposal would create a cycle.',
      );
    }
  }

  const mandatoryBlock =
    authorizeProposedMutation(
      'set-parents',
      ctx,
      request.edgeMemberId,
      before,
      {
        kind:
          'set-parents',
        memberId:
          request.edgeMemberId,
        parentIds:
          proposed,
      },
    );

  if (mandatoryBlock) {
    return mandatoryBlock;
  }

  const authoritySnapshot =
    ctx.members
      .filter(
        member =>
          member.ownerUserId ===
          beforeMember.ownerUserId,
      )
      .map(
        member => ({
          id:
            member.id,

          ownerUserId:
            member.ownerUserId,

          parentIds:
            normalize(
              member.parentIds ??
              [],
            ),

          relationship:
            member.relationship ??
            null,

          side:
            member.side ??
            null,

          approvalStatus:
            member.approvalStatus ??
            null,

          profileState:
            member.profileState ??
            'placeholder',
        }),
      )
      .sort(
        (left, right) =>
          left.id.localeCompare(
            right.id,
          ),
      );

  try {
    const universal =
      await invokeUniversalTreeMutation(
        ctx,
        'CONNECT_EXISTING_ANCESTRY_ATOMIC',
        {
          relationship:
            request.relationship,

          requestedSide:
            request.requestedSide,

          anchorMemberId:
            request.anchorMemberId,

          secondAnchorMemberId:
            request.secondAnchorMemberId,

          edgeMemberId:
            request.edgeMemberId,

          evidenceMemberId:
            request.evidenceMemberId,

          expectedParentIds:
            expected,

          proposedParentIds:
            proposed,

          expectedRelationshipWorkflow:
            request
              .expectedRelationshipWorkflow,

          relationshipWorkflow:
            request
              .relationshipWorkflow,

          expectedRelationshipOverride:
            request
              .expectedRelationshipOverride,

          relationshipOverride:
            request
              .relationshipOverride,

          relationshipKey:
            request.relationshipKey,

          modeApplied:
            request.modeApplied,

          expectedAuthoritySnapshot:
            authoritySnapshot,
        },
      );

    if (universal.ok === false) {
      return fail(
        'set-parents',
        ctx,
        request.edgeMemberId,
        before,
        universal.reason,
        universal.error,
      );
    }

    const payload =
      universal.result;

    if (
      payload.edgeMemberId !==
        request.edgeMemberId ||
      payload.evidenceMemberId !==
        request.evidenceMemberId ||
      payload.modeApplied !==
        request.modeApplied
    ) {
      return fail(
        'set-parents',
        ctx,
        request.edgeMemberId,
        before,
        'Universal ancestry result does not match the requested governed endpoints and mode.',
      );
    }

    const durable: GatewayConnectExistingAncestryResult = {
      status:
        typeof payload.status ===
          'string'
          ? payload.status
          : '',

      edgeMemberId:
        typeof payload.edgeMemberId ===
          'string'
          ? payload.edgeMemberId
          : '',

      evidenceMemberId:
        typeof payload.evidenceMemberId ===
          'string'
          ? payload.evidenceMemberId
          : '',

      parentIds:
        Array.isArray(
          payload.parentIds,
        )
          ? payload.parentIds.filter(
              (
                value,
              ): value is string =>
                typeof value ===
                'string',
            )
          : [],

      relationshipWorkflow:
        payload.relationshipWorkflow &&
        typeof payload
          .relationshipWorkflow ===
          'object'
          ? payload
              .relationshipWorkflow as
              TreeRelationshipWorkflow
          : null,

      relationshipOverride:
        typeof payload
          .relationshipOverride ===
          'string'
          ? payload
              .relationshipOverride
          : null,

      auditId:
        typeof payload.auditId ===
          'string'
          ? payload.auditId
          : '',
    };

    if (
      durable.status !==
        'connected' ||
      !durable.edgeMemberId ||
      !durable.evidenceMemberId ||
      !durable.auditId
    ) {
      return fail(
        'set-parents',
        ctx,
        request.edgeMemberId,
        before,
        'Universal ancestry envelope returned an invalid durable certificate.',
      );
    }

    return allow(
      'set-parents',
      ctx,
      request.edgeMemberId,
      before,
      {
        ...(before ?? {}),
        parentIds:
          durable.parentIds,
      },
      durable,
    );
  } catch (error) {
    return fail(
      'set-parents',
      ctx,
      request.edgeMemberId,
      before,
      'Unexpected error during universal atomic ancestry transaction.',
      error,
    );
  }
}

// =====================================================================
// 5. SET SPOUSE
// =====================================================================

export async function gatewaySetSpouse(
  ctx: GatewayContext,
  aId: string,
  bId: string | null,
): Promise<
  GatewayResult<{
    aId: string;
    bId: string | null;
    verified: boolean;
  }>
> {
  if (!ctx.actingUserId) {
    return block(
      'set-spouse',
      ctx,
      aId,
      null,
      BLOCKER.AUTH_REQUIRED,
      'Sign in required.',
    );
  }

  if (!aId) {
    return block(
      'set-spouse',
      ctx,
      null,
      null,
      BLOCKER.ROW_NOT_FOUND,
      'Member id is required.',
    );
  }

  const a =
    ctx.members.find(
      member =>
        member.id === aId,
    ) ?? null;

  const before =
    snapshotMember(a);

  const mandatoryBlock =
    authorizeProposedMutation(
      'set-spouse',
      ctx,
      aId,
      before,
      {
        kind:
          'set-spouse',
        memberId:
          aId,
        spouseId:
          bId,
      },
    );

  if (mandatoryBlock) {
    return mandatoryBlock;
  }

  if (
    bId !== null &&
    aId === bId
  ) {
    return block(
      'set-spouse',
      ctx,
      aId,
      before,
      BLOCKER.SELF_AS_SPOUSE,
      'A member cannot be their own spouse.',
    );
  }

  const b =
    bId === null
      ? null
      : (
          ctx.members.find(
            member =>
              member.id === bId,
          ) ?? null
        );

  if (a && b) {
    const aParents =
      new Set(
        (a.parentIds ?? [])
          .filter(Boolean),
      );

    const bParents =
      new Set(
        (b.parentIds ?? [])
          .filter(Boolean),
      );

    if (
      aParents.has(b.id) ||
      bParents.has(a.id)
    ) {
      return block(
        'set-spouse',
        ctx,
        aId,
        before,
        BLOCKER.PARENT_CHILD_AS_SPOUSE,
        'These two members are already linked as parent and child.',
      );
    }
  }

  if (
    bId !== null &&
    wouldSpouseEdgeCreateCircularAncestry(
      aId,
      bId,
      ctx.members,
    )
  ) {
    return block(
      'set-spouse',
      ctx,
      aId,
      before,
      BLOCKER.CIRCULAR_ANCESTRY,
      'That spouse link would create a circular ancestry loop.',
    );
  }

  if (a && b) {
    const decision =
      evaluateSpouseAddition({
        anchorMemberId:
          aId,

        candidateMember: {
          existingMemberId:
            b.id,
        },

        members:
          ctx.members,
      });

    if (!decision.ok) {
      const code:
        BlockerCode =
          decision.blocker ===
          'self-as-spouse'
            ? BLOCKER.SELF_AS_SPOUSE
            : decision.blocker ===
                'would-create-circular-ancestry'
              ? BLOCKER.CIRCULAR_ANCESTRY
              : decision.blocker ===
                  'strong-duplicate-suspected'
                ? BLOCKER.DUPLICATE_BLOCKED
                : BLOCKER.PARENT_CHILD_AS_SPOUSE;

      return block(
        'set-spouse',
        ctx,
        aId,
        before,
        code,
        decision.reason ??
          'Spouse link refused.',
      );
    }
  }

  try {
    const universal =
      await invokeUniversalTreeMutation(
        ctx,
        'SET_SPOUSE',
        {
          memberId:
            aId,

          spouseId:
            bId,
        },
      );

    if (universal.ok === false) {
      return fail(
        'set-spouse',
        ctx,
        aId,
        before,
        universal.reason,
        universal.error,
      );
    }

    const payload =
      universal.result;

    if (
      payload.memberId !== aId ||
      payload.spouseId !== bId ||
      payload.verified !== true
    ) {
      return fail(
        'set-spouse',
        ctx,
        aId,
        before,
        'Universal spouse result does not match the requested governed partnership state.',
      );
    }

    return allow(
      'set-spouse',
      ctx,
      aId,
      before,
      {
        ...(before ?? {}),
        spouseId:
          bId,
        verified:
          true,
      },
      {
        aId,
        bId,
        verified:
          true,
      },
    );
  } catch (error) {
    return fail(
      'set-spouse',
      ctx,
      aId,
      before,
      'Unexpected error during universal spouse mutation.',
      error,
    );
  }
}

// =====================================================================
// 6. CLAIM IDENTITY — RETIRED FROM BROWSER AUTHORITY
// =====================================================================
// Claim establishment, release, and rebind are server-transaction lifecycle
// concerns. Browser gateway functions may never write claim identity fields.

// =====================================================================
// 7. RUNTIME PROOF — "Graph Enforcement Active"// =====================================================================
// 8. RUNTIME PROOF — "Graph Enforcement Active"
// =====================================================================
//
// Returns a structured report used by the System Health card. Each check
// is PASS / FAIL with a human-readable description. The card aggregates
// these into one "Graph Enforcement Active" verdict.

export interface EnforcementProofCheck {
  id: string;
  label: string;
  pass: boolean;
  detail: string;
}

export interface EnforcementProofReport {
  active: boolean;
  checks: EnforcementProofCheck[];
  generatedAt: string;
}

export function buildEnforcementProof(opts: {
  members: GraphMember[];
}): EnforcementProofReport {
  const checks: EnforcementProofCheck[] = [];

  // 1. Gateway module is importable (this function being callable proves it).
  checks.push({
    id: 'gateway-loaded',
    label: 'Gateway module loaded',
    pass: typeof gatewayInsertMember === 'function' &&
          typeof gatewayUpdateMember === 'function' &&
          typeof gatewayDeleteMember === 'function' &&
          typeof gatewaySetSpouse === 'function' &&
          typeof gatewaySetParents === 'function',
    detail: 'All five browser Tree gateway entry points are exported; claim identity is server-only.',
  });

  // 2. Duplicate guardrail wired in.
  checks.push({
    id: 'dup-guard',
    label: 'Duplicate prevention runs before insert',
    pass: typeof runDuplicatePreflight === 'function',
    detail: 'runDuplicatePreflight() is invoked from gatewayInsertMember().',
  });

  // 3. Cycle detection wired in.
  checks.push({
    id: 'cycle-guard',
    label: 'Circular ancestry detection runs before relationship write',
    pass: typeof wouldSpouseEdgeCreateCircularAncestry === 'function',
    detail: 'Spouse + parent_ids edges both consult an ancestor walk.',
  });

  // 4. Spouse protection wired in.
  checks.push({
    id: 'spouse-protect',
    label: 'Spouse protection runs before spouse link',
    pass: typeof evaluateSpouseAddition === 'function',
    detail: 'evaluateSpouseAddition() gates every gatewaySetSpouse() call.',
  });

  // 5. Audit log present and accumulating entries.
  let auditPresent = false;
  try {
    auditPresent = typeof recordGraphMutation === 'function';
  } catch {
    auditPresent = false;
  }
  checks.push({
    id: 'audit',
    label: 'Audit log records every mutation attempt',
    pass: auditPresent,
    detail: 'recordGraphMutation() emits an entry per allowed / blocked / failed call.',
  });

  // 6. One-auth-user-→-one-member invariant enforced (reads from current
  //    members snapshot).
  let multiClaimRows = 0;
  try {
    const counts = new Map<string, number>();
    for (const m of opts.members) {
      if (!m.claimedByUserId) continue;
      counts.set(m.claimedByUserId, (counts.get(m.claimedByUserId) ?? 0) + 1);
    }
    for (const c of counts.values()) {
      if (c > 1) multiClaimRows += 1;
    }
  } catch {
    multiClaimRows = -1;
  }
  checks.push({
    id: 'one-claim-per-user',
    label: 'One auth user → at most one claimed member',
    pass: multiClaimRows === 0,
    detail: multiClaimRows === 0
      ? 'No auth user holds multiple claimed rows in the loaded graph.'
      : `${multiClaimRows} auth user(s) hold multiple claimed rows — manual repair needed.`,
  });

  const active = checks.every((c) => c.pass);
  return {
    active,
    checks,
    generatedAt: new Date().toISOString(),
  };
}
